/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  analyticsTagResident,
  sendAnalyticsProviderEvent,
} from './analytics-provider'
import { sanitizeEventParams } from './analytics-events'

/**
 * Real-user Core Web Vitals → the page's analytics tag (AGL-1642).
 *
 * Until this module there was no field performance telemetry of any kind in
 * the repo, and the cost was concrete: AGL-1538 recorded a tenant hydration
 * stall of 30s+ that could never be quantified, and the hydration window is
 * also an ATTRIBUTION hole — a signup CTA clicked before gtag hydrates
 * crosses to the console with no `_gl` linker, deleting the acquisition
 * source. "How long does hydration take for real visitors" was unanswerable.
 *
 * ## Why `web-vitals` → GA4, not Speed Insights or Firebase Performance
 *
 * The GA4 property (`G-YW5PG16YTM`) is already consolidated across every
 * first-party surface, already consent-gated correctly per surface, and
 * already carries the funnel these numbers need to sit beside — slow LCP on
 * the pricing page matters BECAUSE of what it does to `begin_checkout`.
 * Sending CWV there adds zero new subprocessors, zero spend, and one ~2KB
 * lazy chunk; `web-vitals` is Google's own library and is ALREADY in
 * `node_modules` as a dependency of `@firebase/performance`. Vercel Speed
 * Insights would add a vendor surface and a paid tier; Firebase Performance
 * is the weakest web product of the three and puts a second SDK on the
 * tenant's critical path (see AGL-1642 for the full comparison).
 *
 * ## Event shape — GA's own `web_vitals` pattern
 *
 * One event per metric, named by the metric (`LCP`, `CLS`, `INP`, `TTFB`),
 * exactly as web.dev's "send to Google Analytics" snippet shapes them:
 *
 * - `value: delta` — GA sums event `value`, and summing DELTAS is what makes
 *   per-page totals right when a metric reports more than once;
 * - `metric_id` — web-vitals' per-pageview id, the deduplication/grouping key
 *   (needs dimension registration to report on — AGL-1637);
 * - `metric_value` / `metric_delta` — the current value and its change;
 * - `metric_rating` — `good` / `needs-improvement` / `poor`, the one
 *   dimension that makes a report readable without percentile math;
 * - `surface` — same param the link-click listener sends: GA's Hostname
 *   dimension separates domains, `surface` separates product surfaces.
 *
 * ## Delivery — the resident tag, through its vendor's adapter
 *
 * Each metric is handed to the resident analytics tag through the adapter
 * that knows its vendor (`analytics-provider.ts`), marked `measurementOnly`
 * so it reaches the measurement property and no advertising destination
 * sharing the tag's library (AGL-2710). This module names no vendor.
 *
 * ## Consent — the same gate as every other tenant event, plus one nuance
 *
 * On a tenant site a tag exists only once a granting consent state has
 * mounted it (AGL-1498). No tag, no hit.
 *
 * The nuance is TIMING, not posture. TTFB reports the moment the reporter
 * installs, and both tags load late by design — `afterInteractive` on the
 * tenant, the analytics SDK's runtime injection on the console — so "no tag
 * YET" is the normal state for the first metrics of every pageview, granting
 * visitors included. Dropping on first miss would silently discard TTFB (and
 * often LCP) on every surface, which reads in GA exactly like fast pages.
 * So a metric that finds no tag is held IN MEMORY and re-offered briefly
 * (~60s at 500ms); if the tag never arrives it is discarded, unsent.
 *
 * That is not the queue `analytics-events.ts` forbids, and the distinction
 * is worth stating: the forbidden queue replays a visitor's pre-consent
 * BEHAVIOUR (pageviews, clicks — the record consent exists to prevent) after
 * a later grant. These are timings of the page's own load, sitting in the
 * browser's performance timeline regardless; and a tag that loads on a late
 * same-pageview grant reports its own `page_view` for this page at that
 * moment — the vitals that then flush describe the exact pageview the tag
 * itself just reported. A visitor who never grants still produces nothing:
 * no tag ever appears, the buffer dies with the page, unsent.
 *
 * ## Why installing early still measures the whole page
 *
 * `web-vitals` reads buffered `PerformanceObserver` entries, so registration
 * order does not truncate the metrics; and CLS/INP/LCP report at
 * interaction/page-hide time anyway. The library itself is loaded with a
 * dynamic `import()` so the tenant's critical path pays nothing for it.
 *
 * The console's `traffic_type: 'internal'` stamp (AGL-1582) rides these hits
 * unchanged: the analytics SDK's `setDefaultEventParameters` sets a default on
 * the tag itself, which applies to every later event on the page — including
 * these, which reach the same tag (verified against the SDK's `wrapGtag`, not
 * assumed).
 */

/** The four metrics worth a GA event, per web.dev's GA4 guidance. */
const METRIC_HANDLER_NAMES = ['onCLS', 'onINP', 'onLCP', 'onTTFB'] as const

/** How long a metric waits for a tag before being discarded, unsent. */
const TAG_WAIT_INTERVAL_MS = 500
const TAG_WAIT_MAX_TRIES = 120

/** Shape of the web-vitals metric object this module consumes. */
interface ReportedMetric {
  name: string
  id: string
  value: number
  delta: number
  rating?: string
}

export interface WebVitalsReportingOptions {
  /**
   * Which product surface produced the measurement — `site` for a tenant
   * published page, `console` for the console. Same axis as the link-click
   * listener's `surface`.
   */
  surface: string
  /**
   * Whether this visitor's consent currently permits analytics.
   *
   * Optional, and ABSENT MEANS ALLOWED — on a tenant page the absence of a
   * resident tag is already the gate, because the tag only loads for a
   * visitor who granted (AGL-1498), and a self-hosted surface answers to its
   * operator's own configuration.
   *
   * The console needs it because its gate has a different shape. Its
   * analytics SDK injects the tag and a page cannot unload a script, so a
   * visitor who WITHDRAWS mid-session leaves the tag resident — and this
   * module hands it events directly rather than through `deliver()`, so the
   * console's refusing transport does not cover it. Re-read on every delivery rather
   * than captured at install, since the whole point is that the answer
   * changes within one document.
   */
  allowed?: () => boolean
}

let installed = false
let consentGate: (() => boolean) | null = null
let pending: Array<{ name: string; params: Record<string, unknown> }> = []
let watcher: ReturnType<typeof setInterval> | null = null
let watcherTries = 0

/** The consent verdict, with no gate registered reading as "allowed". */
function consentAllows(): boolean {
  if (consentGate === null) return true
  try {
    return consentGate() === true
  } catch {
    // A gate that throws cannot be read as consent — storage can throw in
    // private mode, and "we could not tell" resolves to no.
    return false
  }
}

/** One metric to the measurement property of every resident tag. */
function sendMetricEvent(name: string, params: Record<string, unknown>): void {
  sendAnalyticsProviderEvent(name, params, { measurementOnly: true })
}

function flushPending(): void {
  const held = pending
  pending = []
  for (const event of held) {
    sendMetricEvent(event.name, event.params)
  }
}

function stopWatcher(): void {
  if (watcher !== null) {
    clearInterval(watcher)
    watcher = null
  }
  watcherTries = 0
}

/**
 * Offer one metric to the tag, or hold it while the tag is still loading.
 * See the module comment for why holding briefly is not the forbidden queue.
 */
function deliverMetricEvent(
  name: string,
  params: Record<string, unknown>,
): void {
  // Checked HERE rather than at install, and before anything is held: a
  // withdrawal mid-session has to drop what is already waiting as well as what
  // comes next, or the held metrics simply arrive later against a visitor who
  // has said no.
  if (!consentAllows()) {
    pending = []
    stopWatcher()
    return
  }
  if (analyticsTagResident()) {
    // Anything held arrived before this one; keep the receive order honest.
    if (pending.length) flushPending()
    stopWatcher()
    sendMetricEvent(name, params)
    return
  }
  pending.push({ name, params })
  if (watcher !== null) return
  watcherTries = 0
  watcher = setInterval(() => {
    watcherTries += 1
    // The visitor can refuse while the tag is still being waited for, which is
    // exactly the prior-consent case: no tag yet, metrics held, and a Decline
    // click that must empty the hold rather than let it flush later.
    if (!consentAllows()) {
      pending = []
      stopWatcher()
      return
    }
    if (analyticsTagResident()) {
      flushPending()
      stopWatcher()
      return
    }
    if (watcherTries >= TAG_WAIT_MAX_TRIES) {
      // The tag never came — an ungranted visitor, an ad blocker, or a site
      // with no analytics configured. Dropped, permanently, like every other
      // consentless event.
      pending = []
      stopWatcher()
    }
  }, TAG_WAIT_INTERVAL_MS)
}

function reportMetric(metric: ReportedMetric, surface: string): void {
  // The same sanitizer every payload passes (AGL-1561) — these values are
  // library-generated and carry nothing identifying, but the guarantee should
  // hold because the sanitizer ran, not because this caller was careful.
  const params = sanitizeEventParams({
    // GA sums event `value`; summing DELTAS keeps a twice-reported metric's
    // page total correct. web.dev's canonical GA4 mapping.
    value: metric.delta,
    metric_id: metric.id,
    metric_value: metric.value,
    metric_delta: metric.delta,
    ...(metric.rating ? { metric_rating: metric.rating } : {}),
    surface,
  })
  deliverMetricEvent(metric.name, params)
}

/**
 * Start reporting Core Web Vitals for this page load. Safe to call from
 * render (installs once, touches no render output, never throws) — the same
 * contract as `installLinkClickTracking`, and it is mounted beside it.
 *
 * The library loads via dynamic `import()`: an async chunk off the critical
 * path of the surface whose performance is being measured. If the chunk
 * fails to load there is simply no measurement — never an error.
 */
export function installWebVitalsReporting(
  options: WebVitalsReportingOptions,
): void {
  if (typeof window === 'undefined') return
  if (installed) return
  installed = true
  consentGate = options.allowed ?? null
  const surface = options.surface
  import('web-vitals')
    .then((vitals) => {
      for (const handlerName of METRIC_HANDLER_NAMES) {
        const register = (
          vitals as unknown as Record<
            string,
            ((callback: (metric: ReportedMetric) => void) => void) | undefined
          >
        )[handlerName]
        if (typeof register !== 'function') continue
        try {
          register((metric) => reportMetric(metric, surface))
        } catch {
          // One broken observer must not take the others down.
        }
      }
    })
    .catch(() => {
      // No chunk, no measurement — analytics never breaks the page.
    })
}

/** Test seam — forgets the install, the held metrics and the tag watcher. */
export function resetWebVitalsReporting(): void {
  installed = false
  consentGate = null
  pending = []
  stopWatcher()
}
